/**
 * WorkflowHistoryDetailPage — full audit for a single workflow instance.
 * Admin only.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageContainer } from "@/components/app/PageShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DynamicFormRenderer } from "@/components/app/DynamicFormRenderer";
import {
    ArrowLeft, CheckCircle, X, ClockCounterClockwise, User, UsersThree, ShieldCheck,
    Circle, ArrowRight, ChatCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATE_VISUAL = {
    approved: { icon: CheckCircle, tone: "text-success border-success bg-success/5" },
    rejected: { icon: X, tone: "text-destructive border-destructive bg-destructive/5" },
    pending: { icon: ClockCounterClockwise, tone: "text-warning border-warning bg-warning/5" },
    upcoming: { icon: Circle, tone: "text-muted-foreground border-border" },
    skipped: { icon: Circle, tone: "text-muted-foreground border-border opacity-60" },
};

const ASSIGNEE_ICON = { user: User, group: UsersThree, role: ShieldCheck };

export default function WorkflowHistoryDetailPage() {
    const { recordId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/workflow-history/${recordId}`);
                setData(data);
            } catch (e) {
                toast.error(extractError(e));
            } finally { setLoading(false); }
        })();
    }, [recordId]);

    if (loading) {
        return <PageContainer><div className="text-sm text-muted-foreground">Loading…</div></PageContainer>;
    }
    if (!data) {
        return <PageContainer><div className="text-sm text-muted-foreground">Record not found.</div></PageContainer>;
    }

    const { record, form, fields, sections, steps, history } = data;
    const statusTone = record.status === "approved" ? "border-success text-success" :
        record.status === "rejected" ? "border-destructive text-destructive" : "border-warning text-warning";

    return (
        <PageContainer>
            <div className="mb-6">
                <Link to="/workflow-history" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3" data-testid="wf-detail-back">
                    <ArrowLeft size={12} /> Back to workflow history
                </Link>
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                        <div className="label-overline mb-1">Workflow instance</div>
                        <h1 className="text-3xl font-bold tracking-tight" data-testid="wf-detail-title">{form.form_title || form.form_name}</h1>
                        <p className="text-xs text-muted-foreground font-mono mt-1">
                            <span title={record.id}>{record.id}</span> · {form.form_code}
                        </p>
                    </div>
                    <Badge variant="outline" className={`rounded-sm font-mono text-xs capitalize ${statusTone}`} data-testid="wf-detail-status">{record.status}</Badge>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-6" data-testid="wf-detail-summary">
                <div className="brut-section p-4">
                    <div className="label-overline mb-2">Submitted by</div>
                    <div className="text-sm font-medium">{record.submitted_by?.name || record.submitted_by?.email || "—"}</div>
                    {record.submitted_by?.email && record.submitted_by.name && (
                        <div className="text-xs text-muted-foreground font-mono">{record.submitted_by.email}</div>
                    )}
                </div>
                <div className="brut-section p-4">
                    <div className="label-overline mb-2">Submitted on</div>
                    <div className="text-sm font-mono">{record.submitted_at ? new Date(record.submitted_at).toLocaleString() : "—"}</div>
                </div>
                <div className="brut-section p-4">
                    <div className="label-overline mb-2">Progress</div>
                    <div className="text-sm">
                        {record.status === "pending"
                            ? <span>Step <span className="font-mono font-semibold">{(record.current_step_index || 0) + 1} / {steps.length}</span> · <span className="text-warning">{steps[record.current_step_index]?.name}</span></span>
                            : <span className="capitalize">{record.status} · {steps.length} step{steps.length === 1 ? "" : "s"}</span>}
                    </div>
                </div>
            </div>

            {/* Step trail */}
            <section className="brut-section p-6 mb-6" data-testid="wf-detail-steps">
                <div className="label-overline mb-4">Approval chain</div>
                <ol className="space-y-3">
                    {steps.map((s, i) => {
                        const v = STATE_VISUAL[s.state] || STATE_VISUAL.upcoming;
                        const SI = v.icon;
                        return (
                            <li key={s.index} className={`border rounded-sm p-4 ${v.tone}`} data-testid={`wf-detail-step-${i}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <SI size={20} weight={s.state === "pending" ? "regular" : "fill"} className="mt-0.5" />
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm">#{i + 1} · {s.name || "Untitled step"}</div>
                                            <div className="mt-1.5 text-xs flex items-center gap-2 flex-wrap">
                                                {s.assignees.length === 0
                                                    ? <span className="italic opacity-70">No approvers configured</span>
                                                    : s.assignees.map((a) => {
                                                        const AI = ASSIGNEE_ICON[a.type] || User;
                                                        return (
                                                            <span key={`${a.type}-${a.id}`} className="inline-flex items-center gap-1 bg-card border border-border rounded-sm px-1.5 py-0.5" data-testid={`wf-detail-step-${i}-assignee-${a.id}`}>
                                                                <AI size={11} /> <span className="capitalize text-[10px] opacity-60">{a.type}:</span><span className="font-medium">{a.name || a.id?.slice(0, 8)}</span>
                                                            </span>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="rounded-sm font-mono text-[10px] capitalize border-current">{s.state}</Badge>
                                </div>
                                {s.actions.length > 0 && (
                                    <div className="mt-3 ml-7 space-y-2 border-l-2 border-current pl-3 opacity-95">
                                        {s.actions.map((a, ai) => (
                                            <div key={`${a.at}-${ai}`} className="text-xs" data-testid={`wf-detail-step-${i}-action-${ai}`}>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium capitalize">{a.action}</span>
                                                    <ArrowRight size={10} className="opacity-50" />
                                                    <span>{a.actor_name || a.actor_email || "—"}</span>
                                                    <span className="opacity-60 font-mono">· {a.at ? new Date(a.at).toLocaleString() : ""}</span>
                                                </div>
                                                {a.comment && (
                                                    <div className="mt-1 flex items-start gap-1.5 opacity-85">
                                                        <ChatCircle size={11} className="mt-0.5 flex-shrink-0" />
                                                        <em>"{a.comment}"</em>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            </section>

            {/* Form data */}
            <section className="brut-section p-6 mb-6" data-testid="wf-detail-form">
                <div className="label-overline mb-4">Form data</div>
                <DynamicFormRenderer
                    sections={sections}
                    fields={fields}
                    data={record.data || {}}
                    onChange={() => {}}
                    readOnly
                    layoutMode={form.layout_type || form.layout_mode || "single"}
                />
            </section>

            {/* Raw timeline */}
            <section className="brut-section p-6" data-testid="wf-detail-timeline">
                <div className="label-overline mb-4">Chronological timeline</div>
                {history.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No actions recorded yet.</div>
                ) : (
                    <ul className="space-y-2">
                        {history.map((h, i) => (
                            <li key={`${h.at}-${i}`} className="flex items-start gap-3 text-sm border-l-2 border-border pl-3 py-1" data-testid={`wf-detail-timeline-${i}`}>
                                <Badge variant="outline" className={`rounded-sm font-mono text-[10px] capitalize ${h.action === "approve" ? "border-success text-success" : h.action === "reject" ? "border-destructive text-destructive" : ""}`}>{h.action}</Badge>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs">Step {(h.step ?? 0) + 1}{h.step_name ? ` · ${h.step_name}` : ""} — <span className="font-medium">{h.actor_name || h.actor_email || "—"}</span></div>
                                    {h.comment && <div className="text-xs text-muted-foreground mt-0.5">"{h.comment}"</div>}
                                </div>
                                <div className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </PageContainer>
    );
}
